import React from 'react';

const WorkloadsHelp: React.FC = () => {
  return (
    <>
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Workloads
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Workloads are tag-based resource groupings that let you organize AWS
          infrastructure by business function, application, or team. They drive
          filtering across the platform and can be used for cost allocation.
        </p>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Tag Pairs
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Each workload is defined by one or more tag pairs (key=value) that
          match AWS resource tags:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>AND:</strong> Resource must match all tag pairs connected by AND</li>
          <li><strong>OR:</strong> Resource can match any group separated by OR</li>
          <li>Example: <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">Env=prod AND Team=data</code> matches resources with both tags</li>
          <li>Example: <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">Team=data OR Team=analytics</code> matches either</li>
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Cost Tracking
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          When cost tracking is enabled on a workload, Aivilo Lens allocates
          infrastructure costs from AWS Cost Explorer based on the tag pairs:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li>The primary tag key (most common across your workloads) is used for Cost Explorer grouping</li>
          <li>The tag key must be activated as a <strong>Cost Allocation Tag</strong> in your AWS Billing console</li>
          <li>Costs are attributed to workloads and their linked projects automatically</li>
          <li>View cost breakdowns on the CloudOps FinOps page</li>
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Scope
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Workloads can be scoped to specific AWS accounts or environments:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>All Accounts:</strong> Workload applies across all connected AWS accounts</li>
          <li><strong>Specific accounts/environments:</strong> Workload only matches resources in selected accounts or environments</li>
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          SDK Integration
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Use <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">tracer.set_workload(&quot;workload_name&quot;)</code> in
          your application code to tag AI/agent traces with a workload. This
          connects observability data to infrastructure groupings for end-to-end
          visibility.
        </p>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Linked Projects
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Workloads can be linked to one or more projects. When a workload is
          linked, resources matching the workload automatically inherit the
          project association. This enables project-level cost tracking through
          workload tags.
        </p>
      </div>
    </>
  );
};

export default WorkloadsHelp;
